import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { Lightbulb, Sparkles, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTour } from './TourContext';

/**
 * Tour Overlay Component - displays the spotlight and tooltip
 */
export function TourOverlay() {
  const { activeTour, currentStep, nextStep, prevStep, endTour } = useTour();
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const step = activeTour?.steps[currentStep];

  useEffect(() => {
    if (!step?.target) {
      const frame = requestAnimationFrame(() => {
        setTargetRect(null);
      });
      return () => cancelAnimationFrame(frame);
    }

    const findTarget = () => {
      const element = document.querySelector(step.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
        
        const padding = 16;
        const tooltipWidth = 360;
        const tooltipHeight = 200;
        
        let top, left;
        const placement = step.placement || 'bottom';
        
        switch (placement) {
          case 'top':
            top = rect.top - tooltipHeight - padding;
            left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
            break;
          case 'bottom':
            top = rect.bottom + padding;
            left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
            break;
          case 'left':
            top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
            left = rect.left - tooltipWidth - padding;
            break;
          case 'right':
            top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
            left = rect.right + padding;
            break;
          default:
            top = rect.bottom + padding;
            left = rect.left;
        }
        
        top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeight - padding));
        left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));
        
        setTooltipPosition({ top, left });
      }
    };

    findTarget();
    
    const handleResize = () => findTarget();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);
    
    const retryTimer = setTimeout(findTarget, 500);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
      clearTimeout(retryTimer);
    };
  }, [step]);

  if (!step) return null;

  const isLastStep = currentStep === activeTour.steps.length - 1;
  const isFirstStep = currentStep === 0;

  return createPortal(
    <div className="fixed inset-0 z-[9999]">
      {/* Backdrop with spotlight cutout */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <mask id="spotlight-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left - 8}
                y={targetRect.top - 8}
                width={targetRect.width + 16}
                height={targetRect.height + 16}
                rx="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.6)"
          mask="url(#spotlight-mask)"
        />
      </svg>

      {/* Spotlight ring effect */}
      {targetRect && (
        <div
          className="absolute border-2 border-emerald-400 rounded-lg pointer-events-none animate-pulse"
          style={{
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            boxShadow: '0 0 0 4px rgba(16, 185, 129, 0.2), 0 0 20px rgba(16, 185, 129, 0.4)'
          }}
        />
      )}

      {/* Tooltip */}
      <div
        className="absolute w-[360px] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300"
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
        }}
      >
        {/* Header */}
        <div className={cn(
          "px-5 py-4 flex items-start gap-3",
          step.variant === 'tip' ? 'bg-amber-50' : 
          step.variant === 'important' ? 'bg-blue-50' : 
          'bg-gradient-to-r from-emerald-50 to-teal-50'
        )}>
          <div className={cn(
            "w-10 h-10 rounded-full flex items-center justify-center shrink-0",
            step.variant === 'tip' ? 'bg-amber-100 text-amber-600' :
            step.variant === 'important' ? 'bg-blue-100 text-blue-600' :
            'bg-emerald-100 text-emerald-600'
          )}>
            {step.icon || (
              step.variant === 'tip' ? <Lightbulb className="w-5 h-5" /> :
              <Sparkles className="w-5 h-5" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 text-base">
              {step.title}
            </h3>
            {step.subtitle && (
              <p className="text-sm text-slate-500 mt-0.5">{step.subtitle}</p>
            )}
          </div>
          <button
            onClick={() => endTour(false)}
            className="text-slate-400 hover:text-slate-600 p-1 -m-1 rounded-full hover:bg-slate-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4">
          <p className="text-slate-600 text-sm leading-relaxed">
            {step.content}
          </p>
          
          {step.tips && (
            <ul className="mt-3 space-y-2">
              {step.tips.map((tip, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="text-emerald-500 mt-0.5">•</span>
                  {tip}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {activeTour.steps.map((_, i) => (
              <div
                key={i}
                className={cn(
                  "w-2 h-2 rounded-full transition-all duration-300",
                  i === currentStep 
                    ? "w-6 bg-emerald-500" 
                    : i < currentStep 
                      ? "bg-emerald-300" 
                      : "bg-slate-300"
                )}
              />
            ))}
            <span className="text-xs text-slate-400 ml-2">
              {currentStep + 1} / {activeTour.steps.length}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {!isFirstStep && (
              <Button
                variant="ghost"
                size="sm"
                onClick={prevStep}
                className="text-slate-600"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Tilbake
              </Button>
            )}
            <Button
              size="sm"
              onClick={nextStep}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {isLastStep ? (
                <>
                  Ferdig
                  <Sparkles className="w-4 h-4 ml-1" />
                </>
              ) : (
                <>
                  Neste
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Skip button */}
      <button
        onClick={() => endTour(true)}
        className="fixed bottom-6 left-6 text-white/80 hover:text-white text-sm flex items-center gap-2 transition-colors"
      >
        Hopp over omvisning
      </button>
    </div>,
    document.body
  );
}
